/**
 * CookieConsentBanner — multilingual bottom-sheet consent banner.
 *
 * - Non-blocking: user can still scroll the page behind it.
 * - Auto-detects language (site i18n → navigator.language → fallback FR).
 * - 7 languages: FR, EN, RO, IT, DE, ES, ZH + manual switcher.
 * - Checkboxes for optional cookies (default off) and push reminders (default off, per GDPR).
 * - "Reject all" (prominent, same weight as accept) + "Cookies only" + "Accept all".
 * - Opens from footer link via consent.open().
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Shield, Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConsent } from "@/contexts/ConsentContext";
import { CONSENT_T, CONSENT_LANGS, CONSENT_LANG_LABELS, pickConsentLang } from "@/data/consentTranslations";

const CookieConsentBanner = () => {
  const { i18n } = useTranslation();
  const { bannerOpen, consent, save, reject, acceptAll, close } = useConsent();

  // Pick initial language: site i18n > navigator > previously stored > fr
  const initialLang = useMemo(() => {
    if (consent?.language) return pickConsentLang(consent.language);
    if (i18n?.language) return pickConsentLang(i18n.language);
    if (typeof navigator !== "undefined") return pickConsentLang(navigator.language);
    return "fr";
  }, [consent, i18n]);

  const [lang, setLang] = useState(initialLang);
  const [optCookies, setOptCookies] = useState(consent?.optionalCookies ?? false);
  const [optPush, setOptPush] = useState(consent?.pushReminderIntent ?? false);

  // Keep local state in sync when banner is re-opened with stored values
  useEffect(() => {
    if (bannerOpen) {
      setLang(pickConsentLang(consent?.language || i18n?.language || navigator?.language));
      setOptCookies(consent?.optionalCookies ?? false);
      setOptPush(consent?.pushReminderIntent ?? false);
    }
  }, [bannerOpen, consent, i18n]);

  if (!bannerOpen) return null;

  const t = CONSENT_T[lang] || CONSENT_T.fr;

  const onRejectAll = () => reject(lang);
  const onAcceptCookiesOnly = () => save({ optionalCookies: true, pushReminderIntent: false, language: lang });
  const onAcceptAll = () => acceptAll(lang);
  const onSaveChoices = () => save({ optionalCookies: optCookies, pushReminderIntent: optPush, language: lang });

  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-intro"
      className="fixed inset-x-0 bottom-0 z-[9998] p-3 sm:p-4 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-5xl mx-auto rounded-2xl bg-[#0A0A0A]/95 backdrop-blur-xl border border-[#D4AF37]/25 shadow-[0_-10px_40px_rgba(0,0,0,0.6)]">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
            <Shield size={16} className="text-[#D4AF37]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="consent-title" className="text-white text-base sm:text-lg font-semibold tracking-tight">
              {t.title}
            </h2>
            <p id="consent-intro" className="text-[12.5px] sm:text-[13px] text-white/65 leading-relaxed mt-1.5">
              {t.intro}
            </p>
          </div>
          {/* Language switcher */}
          <select
            data-testid="consent-lang-switch"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="shrink-0 bg-white/5 border border-white/10 rounded-lg text-xs text-white/80 px-2 py-1.5 focus:outline-none focus:border-[#D4AF37]/50"
            aria-label="Language"
          >
            {CONSENT_LANGS.map((code) => (
              <option key={code} value={code} className="bg-[#0A0A0A]">
                {CONSENT_LANG_LABELS[code]}
              </option>
            ))}
          </select>
        </div>

        {/* Checkboxes */}
        <div className="px-5 pb-3 space-y-2">
          <label className="flex items-start gap-3 text-[12.5px] sm:text-[13px] text-white/85 cursor-pointer select-none">
            <input
              data-testid="consent-opt-cookies"
              type="checkbox"
              checked={optCookies}
              onChange={(e) => setOptCookies(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-[#D4AF37] shrink-0"
            />
            <span className="leading-snug">{t.optCookies}</span>
          </label>
          <label className="flex items-start gap-3 text-[12.5px] sm:text-[13px] text-white/85 cursor-pointer select-none">
            <input
              data-testid="consent-opt-push"
              type="checkbox"
              checked={optPush}
              onChange={(e) => setOptPush(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-[#D4AF37] shrink-0"
            />
            <span className="leading-snug flex items-start gap-1.5">
              <Bell size={13} className="text-[#D4AF37]/80 mt-0.5 shrink-0" strokeWidth={1.8} />
              <span>{t.optPush}</span>
            </span>
          </label>

          <p className="text-[11px] text-white/45 leading-snug pt-1">
            {t.note}
          </p>
        </div>

        {/* Buttons — equal weight for Reject / Cookies only / Accept all per GDPR */}
        <div className="px-4 sm:px-5 pb-4 pt-2 flex flex-col sm:flex-row gap-2 sm:gap-2.5 border-t border-white/5">
          <button
            data-testid="consent-reject-btn"
            type="button"
            onClick={onRejectAll}
            className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-white text-sm font-medium transition-colors"
          >
            {t.btnReject}
          </button>
          <button
            data-testid="consent-cookies-only-btn"
            type="button"
            onClick={onAcceptCookiesOnly}
            className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-white text-sm font-medium transition-colors"
          >
            {t.btnCookiesOnly}
          </button>
          {/* Save custom choices only matters if user ticked boxes manually */}
          <button
            data-testid="consent-save-btn"
            type="button"
            onClick={onSaveChoices}
            className="hidden"
            aria-hidden
          />
          <button
            data-testid="consent-accept-all-btn"
            type="button"
            onClick={onAcceptAll}
            className="flex-1 px-4 py-2.5 rounded-xl bg-[#D4AF37] hover:bg-[#B8941F] text-black text-sm font-semibold transition-colors"
          >
            {t.btnAcceptAll}
          </button>
        </div>

        {/* Close (only when banner is re-opened via footer, user already has a decision) */}
        {consent && (
          <button
            data-testid="consent-close-btn"
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          >
            <X size={14} strokeWidth={1.8} />
          </button>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
